import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, CheckCircle, XCircle, Edit, Trash2, Eye, Upload, FileText, Download, Folder, FolderOpen, Settings, Filter, ChevronRight, ArrowLeft, Home, Grid, List, X } from 'lucide-react';
import { ordersAPI } from '../../lib/api.js';
import { getUser } from '../../lib/auth.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';

function OrdersPage() {
  // View and navigation state
  const [viewMode, setViewMode] = useState('hierarchical'); // 'hierarchical', 'list'
  const [currentPath, setCurrentPath] = useState([]); // Breadcrumb navigation
  const [currentLevel, setCurrentLevel] = useState('categories'); // 'categories', 'subcategories'
  const [currentParentId, setCurrentParentId] = useState(null);
  const [currentParent, setCurrentParent] = useState(null);

  // Filters and search
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Modal and form state
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    effective_date: '',
    pdf_file: null,
    category_id: '',
    subcategory_id: ''
  });
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    icon: '📄',
    color: '#3B82F6'
  });
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: '',
    description: '',
    icon: '📋',
    color: '#6B7280',
    category_id: ''
  });

  const queryClient = useQueryClient();

  const { data: orders, isLoading } = useQuery({
    queryKey: ['orders', search, currentPath, currentLevel],
    queryFn: () => {
      const filters = { search };
      
      // Apply navigation-based filters
      if (currentPath.length > 0) {
        const currentItem = currentPath[currentPath.length - 1];
        if (currentItem.type === 'category') {
          filters.category_id = currentItem.id;
        } else if (currentItem.type === 'subcategory') {
          filters.subcategory_id = currentItem.id;
        }
      }
      
      return ordersAPI.getAll(filters);
    },
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 30000,
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['orders-categories'],
    queryFn: () => ordersAPI.categories.getAll(),
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 30000,
  });

  const { data: subcategories, isLoading: subcategoriesLoading } = useQuery({
    queryKey: ['orders-subcategories', currentParentId],
    queryFn: () => currentParentId ? ordersAPI.subcategories.getAll(currentParentId) : Promise.resolve({ data: [] }),
    enabled: !!currentParentId && currentLevel === 'subcategories',
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 30000,
  });

  // File upload handling
  const [uploadingFile, setUploadingFile] = useState(false);

  const createMutation = useMutation({
    mutationFn: (data) => ordersAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => ordersAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      setShowModal(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => ordersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
    },
  });

  // Category mutations
  const createCategoryMutation = useMutation({
    mutationFn: (data) => ordersAPI.categories.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders-categories']);
      setShowCategoryModal(false);
      resetCategoryForm();
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }) => ordersAPI.categories.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders-categories']);
      setShowCategoryModal(false);
      resetCategoryForm();
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id) => ordersAPI.categories.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders-categories']);
    },
  });

  // Subcategory mutations
  const createSubcategoryMutation = useMutation({
    mutationFn: (data) => ordersAPI.subcategories.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders-subcategories']);
      setShowSubcategoryModal(false);
      resetSubcategoryForm();
    },
  });

  const updateSubcategoryMutation = useMutation({
    mutationFn: ({ id, data }) => ordersAPI.subcategories.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders-subcategories']);
      setShowSubcategoryModal(false);
      resetSubcategoryForm();
    },
  });

  const deleteSubcategoryMutation = useMutation({
    mutationFn: (id) => ordersAPI.subcategories.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders-subcategories']);
    },
  });

  const handleView = async (order) => {
    try {
      const token = localStorage.getItem("srpf_auth_token");
      if (!token) {
        alert("You must be logged in to view orders.");
        return;
      }

      // Construct full file URL
      const url = order.file_url?.startsWith("http")
        ? order.file_url
        : `${import.meta.env.VITE_API_URL || "http://localhost:4000"}${order.file_url}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      const blob = await response.blob();

      if (blob.type === "application/pdf") {
        // Already PDF, open directly
        const viewUrl = window.URL.createObjectURL(blob);
        window.open(viewUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => window.URL.revokeObjectURL(viewUrl), 10000);
        return;
      }

      // Not PDF: convert to PDF (basic implementation for text/image files)
      // Only supports text and image files for client-side conversion
      let pdfBlob = null;

      if (blob.type.startsWith("text/")) {
        const text = await blob.text();
        // Use jsPDF for text to PDF conversion
        // Make sure jsPDF is installed: npm install jspdf
        const { jsPDF } = await import("jspdf");
        const doc = new jsPDF();
        doc.text(text, 10, 10);
        pdfBlob = doc.output("blob");
      } else if (blob.type.startsWith("image/")) {
        // Convert image to PDF using jsPDF
        const { jsPDF } = await import("jspdf");
        const imgData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        const doc = new jsPDF();
        doc.addImage(imgData, "JPEG", 10, 10, 180, 160);
        pdfBlob = doc.output("blob");
      } else {
        alert("Unsupported file type for PDF conversion.");
        return;
      }

      // Open the converted PDF
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 10000);
    } catch (error) {
      console.error("Error viewing PDF:", error);
      alert("Error viewing PDF. Please try again.");
    }
  };

  const handleDownload = async (order) => {
    try {
      const token = localStorage.getItem('srpf_auth_token');
      if (!token) {
      alert('You must be logged in to download orders.');
      return;
      }
      // Get the download URL (absolute or relative)
      const url =
      order.file_url?.startsWith('http')
        ? order.file_url
        : `${import.meta.env.VITE_API_URL || 'http://localhost:4000'}${order.file_url}`;
      const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      });
      if (!response.ok) {
      throw new Error('Failed to download order PDF');
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      // Try to extract filename from Content-Disposition header
      const disposition = response.headers.get('Content-Disposition');
      let filename = `${order.title || 'order'}.pdf`;
      if (disposition && disposition.includes('filename=')) {
      filename = disposition.split('filename=')[1].replace(/"/g, '').trim();
      if (!filename.toLowerCase().endsWith('.pdf')) {
        filename = filename.replace(/\.[^/.]+$/, '') + '.pdf';
      }
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF. Please try again.');
    }
  };

  const ordersData = orders?.data || [];

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'danger';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'danger';
      default:
        return 'default';
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      effective_date: '',
      pdf_file: null,
      category_id: '',
      subcategory_id: ''
    });
    setEditingOrder(null);
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      description: '',
      icon: '📄',
      color: '#3B82F6'
    });
    setEditingCategory(null);
  };

  const resetSubcategoryForm = () => {
    setSubcategoryFormData({
      name: '',
      description: '',
      icon: '📋',
      color: '#6B7280',
      category_id: ''
    });
    setEditingSubcategory(null);
  };

  const handleCreate = () => {
    setEditingOrder(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      title: order.title || '',
      effective_date: order.effective_date ? order.effective_date.split('T')[0] : '',
      pdf_file: null, // Do not prefill file
      category_id: order.category_id || '',
      subcategory_id: order.subcategory_id || ''
    });
    setShowModal(true);
  };

  // Category handlers
  const handleCreateCategory = () => {
    setEditingCategory(null);
    resetCategoryForm();
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      icon: category.icon || '📄',
      color: category.color || '#3B82F6'
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = (id) => {
    if (window.confirm('Are you sure you want to delete this category? This will also delete all associated subcategories and orders.')) {
      deleteCategoryMutation.mutate(id);
    }
  };

  // Subcategory handlers
  const handleCreateSubcategory = (categoryId = null) => {
    setEditingSubcategory(null);
    resetSubcategoryForm();
    if (categoryId) {
      setSubcategoryFormData(prev => ({ ...prev, category_id: categoryId }));
    }
    setShowSubcategoryModal(true);
  };

  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryFormData({
      name: subcategory.name || '',
      description: subcategory.description || '',
      icon: subcategory.icon || '📋',
      color: subcategory.color || '#6B7280',
      category_id: subcategory.category_id || ''
    });
    setShowSubcategoryModal(true);
  };

  const handleDeleteSubcategory = (id) => {
    if (window.confirm('Are you sure you want to delete this subcategory? This will also delete all associated orders.')) {
      deleteSubcategoryMutation.mutate(id);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const generateSlug = (text) =>
      text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

    const submitData = {
      title: formData.title,
      effective_date: formData.effective_date,
      slug: generateSlug(formData.title),
      category_id: formData.category_id || null,
      subcategory_id: formData.subcategory_id || null,
    };

    if (editingOrder) {
      updateMutation.mutate({ id: editingOrder.id, data: submitData });
    } else {
      // For new documents, handle file upload
      if (formData.pdf_file) {
        setUploadingFile(true);
        try {
          // Create FormData for file upload and use upload endpoint
          const formDataToSend = new FormData();
          formDataToSend.append('pdf_file', formData.pdf_file);
          Object.keys(submitData).forEach((key) => formDataToSend.append(key, submitData[key]));
          
          // Use upload endpoint instead of create endpoint
          await ordersAPI.uploadPdf(formDataToSend);
          queryClient.invalidateQueries(['orders']);
          setShowModal(false);
          resetForm();
        } catch (error) {
          console.error('Error uploading file:', error);
        } finally {
          setUploadingFile(false);
        }
      } else {
        // For non-file uploads, use regular create endpoint with required fields
        const user = getUser();
        const createData = {
          ...submitData,
          company_id: user?.company_id || 1, // Get from user context
          description: formData.title, // Use title as description for now
        };
        createMutation.mutate(createData);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubcategoryInputChange = (e) => {
    const { name, value } = e.target;
    setSubcategoryFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    const generateSlug = (text) =>
      text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

    const submitData = {
      name: categoryFormData.name,
      description: categoryFormData.description,
      slug: generateSlug(categoryFormData.name),
      icon: categoryFormData.icon,
      color: categoryFormData.color,
    };

    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory.id, data: submitData });
    } else {
      createCategoryMutation.mutate(submitData);
    }
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    const generateSlug = (text) =>
      text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

    const submitData = {
      name: subcategoryFormData.name,
      description: subcategoryFormData.description,
      slug: generateSlug(subcategoryFormData.name),
      icon: subcategoryFormData.icon,
      color: subcategoryFormData.color,
      category_id: parseInt(subcategoryFormData.category_id),
    };

    if (editingSubcategory) {
      updateSubcategoryMutation.mutate({ id: editingSubcategory.id, data: submitData });
    } else {
      createSubcategoryMutation.mutate(submitData);
    }
  };

  const categoriesData = categories?.data?.categories || categories?.categories || [];
  const subcategoriesData = subcategories?.data?.subcategories || subcategories?.subcategories || [];

  // Get current level data based on navigation
  const getCurrentLevelData = () => {
    if (currentLevel === 'categories') {
      return categoriesData;
    } else if (currentLevel === 'subcategories' && currentParentId) {
      return subcategoriesData;
    }
    return [];
  };

  const currentData = getCurrentLevelData();

  // Navigation helper functions
  const navigateTo = (item, type) => {
    const newPath = [...currentPath, { type, id: item.id, name: item.name, fullObject: item }];
    setCurrentPath(newPath);
    setCurrentParentId(item.id);
    setCurrentParent(item);

    if (type === 'category') {
      setCurrentLevel('subcategories');
      // Stay in hierarchical view to show subcategories first
      setViewMode('hierarchical');
    } else {
      // For subcategories, switch to list view to show orders
      setViewMode('list');
    }
  };

  const navigateBack = () => {
    if (currentPath.length === 0) return;

    const newPath = currentPath.slice(0, -1);
    setCurrentPath(newPath);

    if (newPath.length === 0) {
      setCurrentLevel('categories');
      setCurrentParentId(null);
      setCurrentParent(null);
      setViewMode('hierarchical'); // Return to folder view at root
    } else {
      const parentItem = newPath[newPath.length - 1];
      setCurrentParentId(parentItem.id);
      setCurrentParent(parentItem.fullObject);
      setCurrentLevel('subcategories');
      setViewMode('hierarchical'); // Return to hierarchical view to show subcategories
    }
  };

  const navigateToRoot = () => {
    setCurrentPath([]);
    setCurrentLevel('categories');
    setCurrentParentId(null);
    setCurrentParent(null);
    setViewMode('hierarchical'); // Return to folder view at root
  };

  // Render hierarchical category grid
  const renderCategoryGrid = () => {
    if (categoriesLoading || subcategoriesLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      );
    }

    // Ensure currentData is always an array
    const safeCurrentData = Array.isArray(currentData) ? currentData : [];

    if (!safeCurrentData || safeCurrentData.length === 0) {
      // If we're in subcategories view but there are no subcategories, switch to list view to show orders
      if (currentLevel === 'subcategories' && currentParentId) {
        setViewMode('list');
        return null; // Let the list view handle the display
      }

      const emptyMessage = currentLevel === 'categories'
        ? 'No categories'
        : 'No subcategories';
      const createMessage = currentLevel === 'categories'
        ? 'Create your first category to organize orders'
        : 'Create your first subcategory';

      return (
        <div className="text-center py-12">
          <Folder className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{emptyMessage}</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {createMessage}
          </p>
          <Button
            onClick={() => {
              if (currentLevel === 'categories') {
                handleCreateCategory();
              } else {
                handleCreateSubcategory(currentParentId);
              }
            }}
            className="flex items-center gap-2 mx-auto"
          >
            <Plus className="w-4 h-4" />
            Create {currentLevel === 'categories' ? 'Category' : 'Subcategory'}
          </Button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {safeCurrentData.map((item) => {
          // Always allow navigation into categories - we'll check for subcategories/orders after navigation
          const canNavigate = true;

          return (
            <div
              key={item.id}
              className="group relative p-5 rounded-xl border transition-all duration-200 min-h-[180px] flex flex-col border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-md"
              onClick={() => {
                if (canNavigate) {
                  navigateTo(item, currentLevel === 'categories' ? 'category' : 'subcategory');
                } else {
                  // If it's a leaf node, switch to list view to show orders
                  setViewMode('list');
                }
              }}
            >
              {/* Folder Icon */}
              <div className="flex items-center justify-center mb-3 flex-shrink-0">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm"
                  style={{ backgroundColor: item.color + '15', color: item.color }}
                >
                  {canNavigate ? (
                    <FolderOpen className="w-7 h-7" />
                  ) : (
                    <Folder className="w-7 h-7" />
                  )}
                </div>
              </div>

              {/* Folder Info */}
              <div className="text-center flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-base leading-tight">
                    {item.name}
                  </h3>
                  {item.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>
                  )}
                </div>
                <div className="mt-3">
                  {canNavigate ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      Browse
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {item.orderCount || 0} orders
                    </span>
                  )}
                </div>
              </div>

              {/* Action Menu */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-200">
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentLevel === 'categories') {
                        handleEditCategory(item);
                      } else {
                        handleEditSubcategory(item);
                      }
                    }}
                    className="p-1.5 rounded-md bg-white/90 hover:bg-white shadow-sm border border-gray-200 hover:border-gray-300 transition-all duration-200"
                    title="Edit folder"
                  >
                    <Edit className="w-3.5 h-3.5 text-gray-600 hover:text-gray-800" />
                  </button>
                  {currentLevel === 'categories' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateSubcategory(item.id);
                      }}
                      className="p-1.5 rounded-md bg-white/90 hover:bg-white shadow-sm border border-gray-200 hover:border-gray-300 transition-all duration-200"
                      title="Create subcategory"
                    >
                      <Plus className="w-3.5 h-3.5 text-gray-600 hover:text-gray-800" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentLevel === 'categories') {
                        handleDeleteCategory(item.id);
                      } else {
                        handleDeleteSubcategory(item.id);
                      }
                    }}
                    className="p-1.5 rounded-md bg-white/90 hover:bg-white shadow-sm border border-gray-200 hover:border-red-300 transition-all duration-200"
                    title="Delete folder"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-gray-600 hover:text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Add New Folder Card */}
        <div
          className="group relative p-6 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 cursor-pointer transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/10"
          onClick={() => {
            if (currentLevel === 'categories') {
              handleCreateCategory();
            } else {
              handleCreateSubcategory(currentParentId);
            }
          }}
        >
          <div className="flex flex-col items-center justify-center h-full min-h-[160px]">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/20 transition-colors">
              <Plus className="w-8 h-8 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 text-lg">
              New Folder
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center leading-relaxed">
              Create a new folder to organize your orders
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full px-6 py-6 overflow-y-auto dark:text-white">
      {/* Header */}
      <div className="mb-8">
        {currentPath.length > 0 && (
          <div className="mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={navigateBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
        )}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Orders</h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">Organize and manage your orders</p>
          </div>
          <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('hierarchical')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'hierarchical'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              <Folder className="w-4 h-4 inline mr-2" />
              Folders
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'list'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              <List className="w-4 h-4 inline mr-2" />
              List
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                if (currentLevel === 'categories') {
                  handleCreateCategory();
                } else {
                  handleCreateSubcategory(currentParentId);
                }
              }}
              variant="outline"
              className="flex items-center gap-2 border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
            >
              <Folder size={16} />
              New Folder
            </Button>
            <Button onClick={handleCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white">
              <Plus size={16} />
              New Order
            </Button>
          </div>
        </div>
      </div>
      </div>

      {/* Search Bar */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      {viewMode === 'hierarchical' && currentPath.length > 0 && (
        <div className="mb-6">
          <nav className="flex items-center gap-2 text-sm">
            <button
              onClick={navigateToRoot}
              className="flex items-center gap-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </button>
            {currentPath.map((item, index) => (
              <React.Fragment key={item.id}>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className={`${index === currentPath.length - 1
                  ? 'text-gray-900 dark:text-white font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white cursor-pointer'
                  }`}>
                  {item.name}
                </span>
              </React.Fragment>
            ))}
          </nav>
        </div>
      )}

      {/* Main Content */}
      <div className="w-full">
        {viewMode === 'hierarchical' ? (
          /* Hierarchical View - File Manager Style */
          <Card>
            <div className="p-6">
              {/* Navigation Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex-1">
                  {/* <div className="flex items-center gap-4 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {currentLevel === 'categories' ? 'Categories' : 'Subcategories'}
                    </h3>
                    {currentPath.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={navigateBack}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                      </Button>
                    )}
                  </div> */}

                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {currentLevel === 'categories'
                      ? 'Click on categories to browse subcategories and orders'
                      : 'Click on subcategories to view orders'
                    }
                  </p>
                </div>
              </div>
              {renderCategoryGrid()}
            </div>
          </Card>
        ) : (
          /* List View - Traditional Orders List */
          <Card>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : ordersData.length > 0 ? (
              <div className="space-y-4">
                {ordersData.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                          {order.title}
                        </h3>
                        {order.category_name && (
                          <Badge variant="outline" className="text-xs">
                            {order.category_icon} {order.category_name}
                          </Badge>
                        )}
                        {order.subcategory_name && (
                          <Badge variant="outline" className="text-xs">
                            {order.subcategory_icon} {order.subcategory_name}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {order.description?.substring(0, 100)}...
                      </p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(order.created_at)}
                        </span>
                        {order.effective_date && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Effective: {formatDate(order.effective_date)}
                          </span>
                        )}
                        {order.priority && (
                          <Badge variant={order.priority === 'high' ? 'danger' : order.priority === 'medium' ? 'warning' : 'default'}>
                            {order.priority}
                          </Badge>
                        )}
                        <Badge variant={getStatusBadgeVariant(order.status)}>
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleView(order)}
                        title="View PDF"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleDownload(order)}
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(order)} title="Edit Details">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm" 
                        onClick={() => handleDelete(order.id)}
                        disabled={deleteMutation.isPending}
                        title="Delete Document"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No orders found</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {currentPath.length > 0 
                    ? `No orders found in ${currentPath[currentPath.length - 1].name}`
                    : 'Get started by uploading your first order.'
                  }
                </p>
                <div className="mt-6">
                  <Button onClick={handleCreate} className="flex items-center gap-2 mx-auto">
                    <Upload size={16} />
                    Upload Order
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingOrder ? 'Edit Order Document' : 'Upload Order PDF'}
              </h2>
              <Button variant="outline" size="sm" onClick={() => setShowModal(false)}>
                ×
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter order title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  PDF Document
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                  <div className="space-y-1 text-center">
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600 dark:text-gray-400">
                      <label htmlFor="pdf-upload" className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                        <span>Upload a PDF file</span>
                        <input
                          id="pdf-upload"
                          name="pdf_file"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setFormData(prev => ({ ...prev, pdf_file: e.target.files[0] }))}
                          className="sr-only"
                          required={!editingOrder}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      PDF files only, up to 10MB
                    </p>
                    {formData.pdf_file && (
                      <p className="text-sm text-green-600 dark:text-green-400">
                        Selected: {formData.pdf_file.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date
                  </label>
                  <Input
                    name="effective_date"
                    type="date"
                    value={formData.effective_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Category</option>
                    {categoriesData.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.icon} {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.category_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subcategory
                  </label>
                  <select
                    name="subcategory_id"
                    value={formData.subcategory_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="">Select Subcategory</option>
                    {subcategoriesData.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.icon} {subcategory.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={createMutation.isPending || updateMutation.isPending || uploadingFile}
                >
                  {uploadingFile ? 'Uploading...' : 
                   createMutation.isPending || updateMutation.isPending ? 'Saving...' : 
                   (editingOrder ? 'Update' : 'Upload PDF')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingCategory ? 'Edit Category' : 'Create Category'}
              </h2>
              <Button variant="outline" size="sm" onClick={() => setShowCategoryModal(false)}>
                ×
              </Button>
            </div>

            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <Input
                  name="name"
                  value={categoryFormData.name}
                  onChange={handleCategoryInputChange}
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={categoryFormData.description}
                  onChange={handleCategoryInputChange}
                  placeholder="Enter category description"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Icon
                  </label>
                  <Input
                    name="icon"
                    value={categoryFormData.icon}
                    onChange={handleCategoryInputChange}
                    placeholder="📄"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                  </label>
                  <Input
                    name="color"
                    type="color"
                    value={categoryFormData.color}
                    onChange={handleCategoryInputChange}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCategoryModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={createCategoryMutation.isPending || updateCategoryMutation.isPending}
                >
                  {createCategoryMutation.isPending || updateCategoryMutation.isPending ? 'Saving...' : 
                   (editingCategory ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingSubcategory ? 'Edit Subcategory' : 'Create Subcategory'}
              </h2>
              <Button variant="outline" size="sm" onClick={() => setShowSubcategoryModal(false)}>
                ×
              </Button>
            </div>

            <form onSubmit={handleSubcategorySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  name="category_id"
                  value={subcategoryFormData.category_id}
                  onChange={handleSubcategoryInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select Category</option>
                  {categoriesData.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <Input
                  name="name"
                  value={subcategoryFormData.name}
                  onChange={handleSubcategoryInputChange}
                  placeholder="Enter subcategory name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={subcategoryFormData.description}
                  onChange={handleSubcategoryInputChange}
                  placeholder="Enter subcategory description"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Icon
                  </label>
                  <Input
                    name="icon"
                    value={subcategoryFormData.icon}
                    onChange={handleSubcategoryInputChange}
                    placeholder="📋"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Color
                  </label>
                  <Input
                    name="color"
                    type="color"
                    value={subcategoryFormData.color}
                    onChange={handleSubcategoryInputChange}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowSubcategoryModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={createSubcategoryMutation.isPending || updateSubcategoryMutation.isPending}
                >
                  {createSubcategoryMutation.isPending || updateSubcategoryMutation.isPending ? 'Saving...' : 
                   (editingSubcategory ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
